import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllProducts } from '../../services/Product/productService';
import PaginationComponent from '../Pagination/PaginationComponent';
import CreateProductModal from '../Product/CreateProductModal';

const ProductInput = ({ value, onChange, label, isInvalid, userRoles, createdNew }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(value || { id: null, description: '', discount: null });
  const [searchText, setSearchText] = useState(value?.description || '');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedProduct(value || { id: null, description: '', discount: null });
    setSearchText(value?.description || '');
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchProducts = useCallback(async () => {
    if (!shouldFetch) return;
    try {
      const data = await getAllProducts(filterParams, page - 1, pageSize);
      setProducts(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchProducts();
    }
  }, [page, pageSize, filterParams, fetchProducts, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filter = { description: searchText };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setSearchText(product.description);
    setIsReadOnly(true);
    onChange({ description: product.description, id: product.id, discount: product?.discount });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedProduct({ id: null, description: '', discount: null });
    setSearchText('');
    setIsReadOnly(false);
    onChange(null);
  };

  const handleCloseModal = () => {
    if (!selectedProduct.id) {
      setSearchText('');
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
    
    if (filtersChanged) {
      const isOnlyNumbers = /^\d+$/.test(filters.description);
  
      const formattedFilters = {
        ...(isOnlyNumbers ? { oldId: filters.description } : { description: filters.description }),
        active: true
      };

      setFilterParams(formattedFilters);
      setPage(1);
      previousFilterParams.current = formattedFilters;
      setShouldFetch(true);
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? 'is-invalid' : ''}>
          <FormControl
            placeholder="Selecione um Produto"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatório.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Produto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: 'center', display: createdNew ? 'block' : 'none' }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
                setShowCreateModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>
          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>UND</th>
                <th style={{ width: '50px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr key={product.id}>
                  <td>{product.oldId + ' - ' +product.description}</td>
                  <td>{product.unit}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectProduct(product)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: '4px', padding: '5px 10px', minWidth: '40px', height: '30px' }}
                    >
                      <FaCheck style={{ color: 'inherit', opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={products.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <CreateProductModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          setSelectedProduct(created);
          onChange(created);
        }}
      />
    </>
  );
};

export default ProductInput;